import { styled } from "goober";
import { useMemo, useState } from "react";
import {
  getAIProvider,
  resolveAIProviderId,
} from "../../../shared/aiProviders";
import type { MeteredMeasure } from "../../../shared/connectors/types";
import { queries, roundedHoursAgo } from "../../../shared/queries";
import { useQuery } from "../../hooks/useQuery";
import {
  groupByWorkflow,
  usageTypeIcons,
  type WorkflowUsage,
} from "../../lib/connector";
import {
  computeTokensByModel,
  computeTransportTotals,
  formatAverageDuration,
} from "../../lib/connectorUsage";
import { formatBytes } from "../../lib/formatBytes";
import { formatRelativeTime } from "../../lib/formatRelativeTime";
import { makeUrl, routes } from "../../lib/urls";
import { closeModalStack } from "../globalModalStack";
import { CircleMesh } from "../CircleMesh";
import { parseBadgeConfig } from "../CircleMeshEditor";
import { CountBadge } from "../CountBadge";
import { StatCell } from "../Monitoring/AiOverviewStats";
import { formatTokenCount } from "../Monitoring/aiUsage";
import { Dropdown } from "../Dropdown";
import { Icon } from "../Icon";
import { Link } from "../Link";
import { RedactedScopeRow } from "../RedactedScopeRow";
import { SpaceBadge } from "../SpaceBadge";
import { Text } from "../Text";
import { WavyLine } from "../WavyLine";
import { MeasurePricingSection } from "./MeasurePricingSection";
import { UsageLimitsSection } from "./UsageLimitsSection";
import {
  BadgeWrapper,
  BranchLabel,
  StepInfoContainer,
  StepList,
  WorkflowGroup,
  WorkflowHeader,
} from "./usageList";

function branchLabel(name: string): string {
  return name === "main" ? "Live" : name;
}

type Props = {
  connectedAppId: string;
  loading: boolean;
  usageMeasures: readonly MeteredMeasure[] | null;
};

export function UsageTab({ connectedAppId, loading, usageMeasures }: Props) {
  const [nonCommitUsageTotals, nonCommitTotalsResult] = useQuery(
    queries.loadConnectorNonCommitUsageTotals({ connectedAppId })
  );
  const [liveUsageTotals, liveTotalsResult] = useQuery(
    queries.loadConnectorLiveUsageTotals({ connectedAppId })
  );
  const [usageRows, usageResult] = useQuery(
    queries.loadConnectedAppLiveUsage({ connectedAppId })
  );
  const [personalChatUsage, chatResult] = useQuery(
    queries.loadConnectedAppPersonalChatUsage({ connectedAppId })
  );
  const [gitRemoteRows, gitResult] = useQuery(
    queries.loadConnectedAppGitRemoteUsage({ connectedAppId })
  );
  const [auditRows, auditResult] = useQuery(
    queries.loadConnectedAppAuditUsage({ connectedAppId })
  );
  const [aiProviderRows, aiResult] = useQuery(
    queries.loadConnectedAppAiProviderUsage({ connectedAppId })
  );
  const since = roundedHoursAgo(30 * 24);
  const [usageDaily, usageDailyResult] = useQuery(
    queries.connectorUsageDailyLite({ since, connectorIds: [connectedAppId] })
  );

  const transport = useMemo(
    () => computeTransportTotals(usageDaily),
    [usageDaily]
  );
  const tokensByModel = useMemo(
    () => computeTokensByModel(usageDaily),
    [usageDaily]
  );
  const hasMetrics = transport != null || tokensByModel.length > 0;

  // Unfiltered totals per type, in one pass, to redact what RBAC hid.
  const totals = useMemo(() => {
    const stepKeys: string[] = [];
    let chats = 0;
    let gitRemotes = 0;
    let audits = 0;
    let aiProviders = 0;
    for (const u of liveUsageTotals) stepKeys.push(`${u.refId}/${u.subKey}`);
    for (const u of nonCommitUsageTotals) {
      if (u.type === "personal_chat") chats++;
      else if (u.type === "space_git_remote") gitRemotes++;
      else if (u.type === "audit_log_destination") audits++;
      else if (u.type === "ai_provider") aiProviders++;
    }
    return { stepKeys, chats, gitRemotes, audits, aiProviders };
  }, [liveUsageTotals, nonCommitUsageTotals]);

  const groups = useMemo(() => groupByWorkflow(usageRows), [usageRows]);

  const hiddenStepCount = useMemo(() => {
    const visible = new Set<string>();
    for (const row of usageRows)
      for (const u of row.headCommit?.appUsages ?? [])
        visible.add(`${u.refId}/${u.subKey}`);
    return totals.stepKeys.filter((key) => !visible.has(key)).length;
  }, [totals.stepKeys, usageRows]);
  const hiddenChatCount = Math.max(0, totals.chats - personalChatUsage.length);
  const hiddenGitCount = Math.max(0, totals.gitRemotes - gitRemoteRows.length);
  const hiddenAuditCount = Math.max(0, totals.audits - auditRows.length);
  const hiddenAiCount = Math.max(0, totals.aiProviders - aiProviderRows.length);

  const hasWorkflows = groups.length > 0 || hiddenStepCount > 0;
  const hasChats = personalChatUsage.length > 0 || hiddenChatCount > 0;
  const hasGitRemotes = gitRemoteRows.length > 0 || hiddenGitCount > 0;
  const hasAudit = auditRows.length > 0 || hiddenAuditCount > 0;
  const hasAiProviders = aiProviderRows.length > 0 || hiddenAiCount > 0;

  const stillLoading =
    loading ||
    nonCommitTotalsResult?.type !== "complete" ||
    liveTotalsResult?.type !== "complete" ||
    usageResult?.type !== "complete" ||
    chatResult?.type !== "complete" ||
    gitResult?.type !== "complete" ||
    auditResult?.type !== "complete" ||
    aiResult?.type !== "complete" ||
    usageDailyResult?.type !== "complete";

  if (stillLoading) {
    return (
      <LoadingContainer>
        <WavyLine />
      </LoadingContainer>
    );
  }

  return (
    <Container>
      {hasMetrics && (
        <WorkflowSection>
          <WorkflowGroup>
            <WorkflowHeader>
              <Text
                variant="text-regular-md"
                color="var(--text-primary)"
                ellipsis
              >
                Activity in the last 30 days
              </Text>
            </WorkflowHeader>
            {transport && (
              <StatsRow>
                {[
                  {
                    label: "Requests",
                    value: transport.requests.toLocaleString("en-US"),
                  },
                  {
                    label: "Errors",
                    value: transport.errors.toLocaleString("en-US"),
                  },
                  { label: "Bytes in", value: formatBytes(transport.bytesIn) },
                  {
                    label: "Bytes out",
                    value: formatBytes(transport.bytesOut),
                  },
                  {
                    label: "Avg. duration",
                    value: formatAverageDuration(
                      transport.durationMs,
                      transport.requests
                    ),
                  },
                ].map((stat) => (
                  <StatCell key={stat.label}>
                    <Text variant="text-small" color="var(--text-secondary)">
                      {stat.label}
                    </Text>
                    <Text variant="text-regular-md">{stat.value}</Text>
                  </StatCell>
                ))}
              </StatsRow>
            )}
            {tokensByModel.length > 0 && (
              <StepList>
                {tokensByModel.map((model) => {
                  const parts = [
                    `${formatTokenCount(model.inputTokens)} in`,
                    `${formatTokenCount(model.outputTokens)} out`,
                  ];
                  if (model.cacheReadTokens > 0) {
                    parts.push(
                      `${formatTokenCount(model.cacheReadTokens)} cache read`
                    );
                  }
                  if (model.cacheWriteTokens > 0) {
                    parts.push(
                      `${formatTokenCount(model.cacheWriteTokens)} cache write`
                    );
                  }
                  return (
                    <TokenModelRow key={model.model}>
                      <StepColor style={{ background: "var(--alpha8)" }} />
                      <StepInfoContainer>
                        <Text variant="text-regular-md" ellipsis>
                          {model.model}
                        </Text>
                        <Text
                          variant="text-small"
                          color="var(--text-secondary)"
                          ellipsis
                        >
                          {parts.join(" · ")}
                        </Text>
                      </StepInfoContainer>
                      <Text
                        variant="text-small-md"
                        color="var(--text-secondary)"
                      >
                        {formatTokenCount(model.totalTokens)} tokens
                      </Text>
                    </TokenModelRow>
                  );
                })}
              </StepList>
            )}
          </WorkflowGroup>
        </WorkflowSection>
      )}
      {usageMeasures && (
        <>
          <MeasurePricingSection
            connectorId={connectedAppId}
            rows={usageDaily}
            measures={usageMeasures}
          />
          <UsageLimitsSection connectorId={connectedAppId} />
        </>
      )}
      {hasWorkflows && (
        <WorkflowSection>
          {groups.map((group) => (
            <WorkflowUsageGroup key={group.workflowId} group={group} />
          ))}
          <RedactedScopeRow count={hiddenStepCount} scopeKind="step" />
        </WorkflowSection>
      )}
      {hasChats && (
        <UsageListSection
          iconName={usageTypeIcons.personal_chat}
          title="Chats"
          rows={personalChatUsage}
          hiddenCount={hiddenChatCount}
          scopeKind="chat"
          renderRow={(row) => {
            const pc = row.personalChat;
            if (!pc) return null;
            return {
              key: pc.id,
              url: makeUrl(routes.chats, {
                tabId: pc.id,
                userId: pc.userId,
                ...closeModalStack(),
              }),
              primary: (
                <Text variant="text-regular-md" ellipsis>
                  {pc.name}
                </Text>
              ),
              secondary: pc.user?.name,
            };
          }}
        />
      )}
      {hasGitRemotes && (
        <UsageListSection
          iconName={usageTypeIcons.space_git_remote}
          title="Git sync"
          rows={gitRemoteRows}
          hiddenCount={hiddenGitCount}
          scopeKind="git remote"
          renderRow={(row) => {
            const space = row.space;
            if (!space) return null;
            return {
              key: row.refId,
              url: makeUrl(routes.modal.space, { id: space.id }),
              primary: (
                <Text variant="text-regular-md" ellipsis>
                  {space.name}
                </Text>
              ),
              secondary: row.spaceGitRemote?.remoteUrl,
            };
          }}
        />
      )}
      {hasAudit && (
        <UsageListSection
          iconName={usageTypeIcons.audit_log_destination}
          title="Audit log destinations"
          rows={auditRows}
          hiddenCount={hiddenAuditCount}
          scopeKind="audit log destination"
          renderRow={(row) => {
            const dest = row.auditLogDestination;
            if (!dest) return null;
            return {
              key: row.refId,
              url: makeUrl(routes.settings.auditLogs.destinations, {
                destinationId: dest.id,
                ...closeModalStack(),
              }),
              primary: (
                <Text variant="text-regular-md" ellipsis>
                  {dest.name}
                </Text>
              ),
              secondary: dest.url,
            };
          }}
        />
      )}
      {hasAiProviders && (
        <UsageListSection
          iconName={usageTypeIcons.ai_provider}
          title="AI providers"
          rows={aiProviderRows}
          hiddenCount={hiddenAiCount}
          scopeKind="AI provider"
          renderRow={(row) => {
            const provider = row.aiProvider;
            if (!provider) return null;
            return {
              key: row.refId,
              url: makeUrl(routes.settings.ai, {
                configId: provider.id,
                ...closeModalStack(),
              }),
              primary: (
                <Text variant="text-regular-md" ellipsis>
                  {getAIProvider(resolveAIProviderId(provider)).label}
                </Text>
              ),
              secondary: provider.baseUrl ?? undefined,
            };
          }}
        />
      )}
    </Container>
  );
}

function WorkflowUsageGroup({ group }: { group: WorkflowUsage }) {
  const [selectedBranchId, setSelectedBranchId] = useState(
    group.usageBranches[0]?.branchId
  );
  const selected =
    group.usageBranches.find((b) => b.branchId === selectedBranchId) ??
    group.usageBranches[0];

  const counterMap = useMemo(() => {
    const m = new Map<string, { count: number; lastRunAt: number | null }>();
    for (const c of group.stepActivity) {
      if (c.branchId !== selected?.branchId) continue;
      m.set(c.stepDir, {
        count: c.executionCount ?? 0,
        lastRunAt: c.latestCreatedAt ?? null,
      });
    }
    return m;
  }, [group.stepActivity, selected?.branchId]);

  const badgeConfig = parseBadgeConfig(group.badgeSeed, group.workflowId);
  const hasLiveBranch = group.branches.some((b) => b.name === "main");

  if (!selected) return null;

  return (
    <WorkflowGroup>
      <WorkflowHeader>
        <BadgeWrapper style={hasLiveBranch ? undefined : { opacity: 0.5 }}>
          <CircleMesh
            key={group.badgeSeed}
            targetCircleSize={8}
            seed={badgeConfig.seed}
            mutingIntensity={0}
            animate={false}
            restrictedColors={badgeConfig.colors}
          />
        </BadgeWrapper>
        <Text variant="text-regular-md" color="var(--text-primary)" ellipsis>
          {group.workflowName}
        </Text>
        <SpaceBadge space={group.space} />
        <BranchControl>
          {group.usageBranches.length > 1 ? (
            <Dropdown
              align="bottom"
              minWidth={180}
              opener={
                <BranchPickerOpener>
                  <Icon
                    name="git-branch"
                    size={12}
                    color="var(--text-secondary)"
                  />
                  <Text
                    variant="text-small"
                    color="var(--text-secondary)"
                    ellipsis
                  >
                    {branchLabel(selected.branchName)}
                  </Text>
                  <Icon
                    name="chevron-down"
                    size={12}
                    color="var(--text-secondary)"
                  />
                </BranchPickerOpener>
              }
              sections={[
                {
                  items: group.usageBranches.map((b) => ({
                    type: "button" as const,
                    key: b.branchId,
                    left: (
                      <Icon
                        name="git-branch"
                        size={16}
                        color="var(--text-secondary)"
                      />
                    ),
                    label: branchLabel(b.branchName),
                    selected: b.branchId === selected.branchId,
                    right:
                      b.branchId === selected.branchId ? (
                        <Icon name="check" size={12} />
                      ) : undefined,
                    onClick: () => setSelectedBranchId(b.branchId),
                  })),
                },
              ]}
            />
          ) : (
            <BranchLabel>
              <Icon name="git-branch" size={12} color="var(--text-secondary)" />
              <Text variant="text-small" color="var(--text-secondary)" ellipsis>
                {branchLabel(selected.branchName)}
              </Text>
            </BranchLabel>
          )}
        </BranchControl>
      </WorkflowHeader>

      <StepList>
        {selected.steps.map((step) => {
          const url = makeUrl(routes.space.workflow, {
            spaceId: step.spaceId,
            workflowId: step.workflowId,
            step: step.stepDir,
            ...closeModalStack(),
          });
          const entry = counterMap.get(step.stepDir);
          const count = entry?.count ?? 0;
          const lastRun = entry?.lastRunAt ?? null;
          return (
            <StepRow key={step.stepDir} to={url}>
              <StepColor style={{ background: `var(--${step.color}-600)` }} />
              <StepInfoContainer>
                <StepNameRow>
                  <Text variant="text-regular-md" ellipsis>
                    {step.stepDir}
                  </Text>
                  <CountBadge
                    style={{
                      padding: "1px 6px",
                      background: "var(--alpha4)",
                      borderRadius: 8,
                      flexShrink: 0,
                    }}
                  >
                    <Text variant="text-small-md" color="var(--text-secondary)">
                      {count.toLocaleString()}
                    </Text>
                  </CountBadge>
                </StepNameRow>
                <Text variant="text-small" color="var(--text-secondary)">
                  Last run {lastRun ? formatRelativeTime(lastRun) : "never"}
                </Text>
              </StepInfoContainer>
            </StepRow>
          );
        })}
      </StepList>
    </WorkflowGroup>
  );
}

type UsageRowContent = {
  key: string;
  url: string;
  primary: React.ReactNode;
  secondary?: React.ReactNode;
};

/** A flat list of linked usage rows (chats, git remotes, audit destinations). */
function UsageListSection<T>({
  iconName,
  title,
  rows,
  renderRow,
  hiddenCount = 0,
  scopeKind,
}: {
  iconName: React.ComponentProps<typeof Icon>["name"];
  title: string;
  rows: readonly T[];
  renderRow: (row: T) => UsageRowContent | null;
  hiddenCount?: number;
  scopeKind?: string;
}) {
  return (
    <WorkflowSection>
      <WorkflowGroup>
        <WorkflowHeader>
          <Icon name={iconName} size={16} color="var(--text-primary)" />
          <Text variant="text-regular-md" color="var(--text-primary)" ellipsis>
            {title}
          </Text>
        </WorkflowHeader>
        <StepList>
          {rows.map((row) => {
            const content = renderRow(row);
            if (!content) return null;
            return (
              <StepRow key={content.key} to={content.url}>
                <StepColor style={{ background: "var(--alpha8)" }} />
                <StepInfoContainer>
                  <StepNameRow>{content.primary}</StepNameRow>
                  {content.secondary != null && (
                    <Text
                      variant="text-small"
                      color="var(--text-secondary)"
                      ellipsis
                    >
                      {content.secondary}
                    </Text>
                  )}
                </StepInfoContainer>
              </StepRow>
            );
          })}
          {scopeKind && (
            <RedactedScopeRow count={hiddenCount} scopeKind={scopeKind} />
          )}
        </StepList>
      </WorkflowGroup>
    </WorkflowSection>
  );
}

const Container = styled("div")`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const LoadingContainer = styled("div")`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
`;

const WorkflowSection = styled("div")`
  display: flex;
  flex-direction: column;
  gap: 24px;
  background: var(--surface-secondary);
  border: 0.5px solid var(--border-alpha);
  border-radius: 12px;
  padding: 24px;
  box-sizing: border-box;
`;

const BranchControl = styled("div")`
  margin-left: auto;
  flex-shrink: 0;
  min-width: 0;
`;

const BranchPickerOpener = styled("div")`
  display: flex;
  align-items: center;
  gap: 4px;
  min-width: 0;
  padding: 4px 8px;
  border-radius: 8px;
  transition: background-color 0.1s ease-out;

  &:hover {
    background: var(--alpha4);
  }
`;

const StepRow = styled(Link)`
  display: flex;
  align-items: center;
  padding: 12px;
  gap: 12px;
  text-decoration: none;
  color: inherit;
  cursor: pointer;

  &:hover {
    background: var(--alpha4);
  }

  & + & {
    border-top: 0.5px solid var(--border-alpha);
  }
`;

const StepColor = styled("div")`
  width: 3px;
  height: 24px;
  border-radius: 2px;
  flex-shrink: 0;
`;

const StepNameRow = styled("div")`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const StatsRow = styled("div")`
  display: flex;
  flex-wrap: wrap;
  gap: 16px 40px;
  padding: 0 8px;
`;

const TokenModelRow = styled("div")`
  display: flex;
  align-items: center;
  padding: 12px;
  gap: 12px;

  & + & {
    border-top: 0.5px solid var(--border-alpha);
  }
`;
